import { useEffect, useRef, useState } from "react";
import { EMULATOR_HOST, EMULATOR_PORTS, USE_EMULATORS } from "../utils/emulators";

/**
 * Probe target: a tiny 204 endpoint on Google static infrastructure.
 * Used because it's CORS-safe (we use `no-cors`), always available, and
 * cheap. We only care whether the request completes, not its body.
 */
const PROBE_URL = USE_EMULATORS
  ? // Against the local emulators "online" means the emulators are up — and a
    // sandbox or CI browser may have no route to gstatic at all.
    `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}/`
  : "https://www.gstatic.com/generate_204";

/** How long each individual probe is allowed to take before giving up. */
const PROBE_TIMEOUT_MS = 5000;

/** How often to probe while we believe we're online. */
const POLL_INTERVAL_MS = 20000;

/**
 * The latest verdict from any mounted `useOnlineStatus`, or null before the
 * first one lands. For plain functions (photo upload) that need to know
 * whether to bother without mounting another probe loop of their own; the
 * app-wide network bridge keeps one running at all times.
 */
let lastKnownOnline: boolean | null = null;

export function getLastKnownOnlineStatus(): boolean | null {
  return lastKnownOnline;
}

/**
 * Performs a single network probe with a short timeout. Returns true if the
 * request reached the network within PROBE_TIMEOUT_MS, false otherwise.
 */
async function probeConnectivity(): Promise<boolean> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return false;
  }

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    // `no-cors` returns an opaque response we can't read, but fetch still
    // resolves when the network request completes — which is all we need.
    // Cache-bust so service workers / proxies don't mask the result.
    await fetch(`${PROBE_URL}?_=${Date.now()}`, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    clearTimeout(timer);
    return true;
  } catch {
    return false;
  }
}

/**
 * Tracks whether the browser has *real* network connectivity (not just
 * `navigator.onLine`, which can stay `true` on captive portals and flaky
 * wifi).
 *
 * The hook:
 *   - Seeds offline (pessimistic) and confirms via a probe before reporting online
 *   - Listens to `online` / `offline` events (instant for definite changes)
 *   - Probes `gstatic.com/generate_204` with a short timeout to confirm
 *     real reachability when the browser claims it's online
 *   - Polls every POLL_INTERVAL_MS to catch silent degradation
 *   - Re-probes on tab visibility change
 *
 * This lets the offline UI flip within ~5s of losing real connectivity
 * instead of waiting for Firestore's long-polling timeout (~60s).
 */
export function useOnlineStatus(): boolean {
  // Seed pessimistically (offline) rather than trusting navigator.onLine, which
  // is optimistic and often lies (captive portals, cruise wifi). Combined with
  // the cache-first Firestore boot (see firebase.ts), this keeps the app on the
  // local cache until a real reachability probe confirms we can reach the
  // network — so we never re-enable the network (and risk the long-poll hang)
  // before we've verified it. The initial probe runs immediately on mount, so
  // a genuinely online device flips to online within a probe round-trip.
  const [isOnline, setIsOnline] = useState(false);

  // Track the latest value in a ref so the polling loop can read it
  // without re-subscribing every time state changes.
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  useEffect(() => {
    let cancelled = false;

    const updateStatus = (next: boolean) => {
      if (cancelled) return;
      lastKnownOnline = next;
      if (isOnlineRef.current !== next) {
        isOnlineRef.current = next;
        setIsOnline(next);
      }
    };

    // Probes overlap (poll + online event + visibility can all fire within
    // one timeout window), and a slow one can finish after a newer one. Only
    // the most recently *started* probe may report, so a stale timeout can't
    // knock us offline right after a fresh success (or vice versa).
    let generation = 0;

    const runProbe = async () => {
      const mine = ++generation;
      const ok = await probeConnectivity();
      if (mine !== generation) return;
      updateStatus(ok);
    };

    const handleOnline = () => {
      // Browser thinks we're online — verify before trusting it.
      void runProbe();
    };

    const handleOffline = () => {
      // Browser is confident we're offline; flip immediately. Bump the
      // generation too, so a probe that started before the drop can't land
      // afterwards and flip us back online.
      generation++;
      updateStatus(false);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void runProbe();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibility);

    // Kick off an initial probe so we converge on reality quickly on load.
    void runProbe();

    // Periodic poll to catch silent degradation (captive portal, wifi
    // on-but-no-internet). Runs regardless of current state — a probe is
    // cheap and lets us recover quickly in either direction.
    const pollId = window.setInterval(() => {
      void runProbe();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(pollId);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return isOnline;
}
